import { useEffect, useState } from 'react';
import { adminAPI } from '../../api/client';
import { useToastStore } from '../../store/toastStore';

// UI에 노출할 설정 필드 정의 — 신규 키 추가 시 여기에 한 줄만 더하면 됨.
const FIELDS = [
  {
    key: 'reservation.delay.up_ms',
    label: '등교 예약 딜레이 (UP)',
    unit: 'ms',
    hint: '정시 도달 후 등교 노선 예약까지 추가 대기 시간',
    min: 0,
    max: 30000,
    step: 100,
  },
  {
    key: 'reservation.delay.down_ms',
    label: '하교 예약 딜레이 (DOWN)',
    unit: 'ms',
    hint: '정시 도달 후 하교 노선 예약까지 추가 대기 시간',
    min: 0,
    max: 30000,
    step: 100,
  },
  {
    key: 'reservation.delay.jitter_ms',
    label: '랜덤 지터',
    unit: '±ms',
    hint: '딜레이에 균등분포로 ±N ms 변동 — 사람 패턴 시뮬레이션',
    min: 0,
    max: 5000,
    step: 100,
  },
  {
    key: 'reservation.delay.scout_warmup_ms',
    label: '스카우트 워밍업',
    unit: 'ms',
    hint: '정시 직후 스카우트가 버스 오픈을 확인하기까지의 최소 대기',
    min: 0,
    max: 5000,
    step: 100,
  },
];

function asNumber(v) {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function AdminSettings() {
  const showToast = useToastStore((s) => s.showToast);
  const [serverValues, setServerValues] = useState({}); // 마지막 저장된 값
  const [drafts, setDrafts] = useState({}); // 입력 중 값
  const [meta, setMeta] = useState({}); // updated_at, updated_by
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getSettings();
      if (!res.success) throw new Error(res.error || '조회 실패');
      const byKey = {};
      const metaByKey = {};
      (res.data || []).forEach((row) => {
        byKey[row.key] = row.value;
        metaByKey[row.key] = {
          updated_at: row.updated_at,
          updated_by: row.updated_by,
          description: row.description,
        };
      });
      setServerValues(byKey);
      setDrafts(byKey);
      setMeta(metaByKey);
    } catch (err) {
      showToast(err.message || '설정 조회 실패', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (field) => {
    const draft = asNumber(drafts[field.key]);
    if (draft == null) {
      showToast('숫자 값을 입력해주세요', 'error');
      return;
    }
    if (draft < field.min || draft > field.max) {
      showToast(`${field.min}~${field.max} 범위 값이어야 합니다`, 'error');
      return;
    }
    setSavingKey(field.key);
    try {
      const res = await adminAPI.updateSetting(field.key, draft, field.hint);
      if (!res.success) throw new Error(res.error || '저장 실패');
      setServerValues((p) => ({ ...p, [field.key]: draft }));
      setMeta((p) => ({
        ...p,
        [field.key]: {
          updated_at: res.data?.updated_at || new Date().toISOString(),
          updated_by: res.data?.updated_by || null,
          description: res.data?.description || field.hint,
        },
      }));
      showToast(`${field.label} 저장됨`, 'success');
    } catch (err) {
      showToast(err.message || '저장 실패', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const isDirty = (key) =>
    asNumber(drafts[key]) !== asNumber(serverValues[key]);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-charcoal">시스템 설정</h1>
          <p className="text-sm text-sub-text mt-1">
            자동예약 동작을 즉시 변경합니다. 저장 즉시 다음 실행분부터 적용됩니다.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-press px-3 py-2 text-sm font-semibold text-sub-text border border-border rounded-lg hover:bg-snow disabled:opacity-50"
        >
          새로고침
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-border p-12 text-center">
          <div className="inline-block w-6 h-6 border-2 border-tayo border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-sub-text mt-3">불러오는 중...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* 사람-패턴 딜레이 섹션 */}
          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border bg-snow/40">
              <h2 className="text-sm font-bold text-charcoal">자동예약 사람-패턴 딜레이</h2>
              <p className="text-xs text-sub-text mt-1 leading-relaxed">
                정시 도달 후 실제 예약까지의 추가 대기. UP/DOWN 별로 다르게 설정 가능하며,
                ±지터로 매번 약간씩 다른 값이 적용되어 매크로 시그니처를 통계적으로 제거합니다.
              </p>
            </div>

            <div className="divide-y divide-border">
              {FIELDS.map((field) => {
                const dirty = isDirty(field.key);
                const m = meta[field.key];
                return (
                  <div key={field.key} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-sm font-semibold text-charcoal">
                          {field.label}
                        </label>
                        <p className="text-xs text-sub-text mt-0.5">{field.hint}</p>
                        {m?.updated_at && (
                          <p className="text-[10px] text-sub-text/60 mt-1">
                            마지막 수정: {new Date(m.updated_at).toLocaleString('ko-KR')}
                            {m.updated_by ? ` · ${m.updated_by}` : ''}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 bg-snow border border-border rounded-lg px-2 py-1.5">
                          <input
                            type="number"
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            value={drafts[field.key] ?? ''}
                            onChange={(e) =>
                              setDrafts((p) => ({ ...p, [field.key]: e.target.value }))
                            }
                            className="w-24 bg-transparent text-right font-mono text-sm font-semibold text-charcoal outline-none"
                          />
                          <span className="text-xs text-sub-text">{field.unit}</span>
                        </div>
                        <button
                          onClick={() => save(field)}
                          disabled={!dirty || savingKey === field.key}
                          className={`btn-press px-3 py-2 text-xs font-bold rounded-lg ${
                            dirty
                              ? 'bg-tayo text-white hover:bg-tayo-dark'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          } disabled:opacity-50`}
                        >
                          {savingKey === field.key ? '저장 중' : '저장'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 안내 */}
          <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-4">
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>주의:</strong> 딜레이가 너무 길면 인기 좌석을 놓칠 수 있고, 너무 짧으면
              매크로 시그니처가 드러날 수 있습니다. 기본값(UP 7000ms / DOWN 5000ms / ±1000ms)이
              실제 사람 클릭 분포와 가장 가깝습니다.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
